import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Divider,
  IconButton, 
  useTheme
} from '@mui/material';

import {
  AddShoppingCart as AddItemIcon,
  Restaurant as DineInIcon,
  LocalMall as RetailIcon,
  ShoppingBag as TakeoutIcon,
  LocalOffer as PromotionIcon,
  Storefront as StoreIcon
} from '@mui/icons-material';
import { POSProvider } from '../../contexts/POSContext';
import POSLayout from '../../layouts/POSLayout';
import { useNavigate } from 'react-router-dom';

const POSDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeOrdersCount] = useState(3);
  const [todaysSales] = useState(1249.99);

  const quickActions = [
    { 
      title: 'New Retail Order', 
      icon: <RetailIcon fontSize="large" sx={{ mb: 1 }} />,
      action: () => navigate('/pos/new-order/retail'),
      color: theme.palette.primary.main 
    },
    { 
      title: 'New Dine-In Order', 
      icon: <DineInIcon fontSize="large" sx={{ mb: 1 }} />,
      action: () => navigate('/pos/new-order/dine-in'),
      color: theme.palette.secondary.main 
    },
    { 
      title: 'New Take-Out Order', 
      icon: <TakeoutIcon fontSize="large" sx={{ mb: 1 }} />,
      action: () => navigate('/pos/new-order/takeout'),
      color: theme.palette.success.main 
    },
  ];

  const dashboardCards = [
    {
      title: 'Active Orders',
      content: activeOrdersCount.toString(),
      action: 'View Orders',
      onClick: () => navigate('/pos/orders'),
      color: theme.palette.info.main
    },
    {
      title: 'Today\'s Sales',
      content: `$${todaysSales.toFixed(2)}`,
      action: 'View Details',
      onClick: () => navigate('/pos/sales'),
      color: theme.palette.success.main
    },
    {
      title: 'Store Inventory',
      content: 'Manage Products',
      action: 'Go to Inventory',
      onClick: () => navigate('/pos/products'),
      color: theme.palette.warning.main
    },
    {
      title: 'Promotions',
      content: 'Create & Manage',
      action: 'View Promotions',
      onClick: () => navigate('/pos/promotions'),
      color: theme.palette.error.main
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        POS Dashboard
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={3}>
          {quickActions.map((action, index) => (
            <Grid
              key={index}
              xs={12}
              md={4}>
              <Card 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
                onClick={action.action}
              >
                <Box sx={{ color: action.color }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" align="center">
                  {action.title}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Typography variant="h6" gutterBottom>
        Dashboard
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardCards.map((card, index) => (
          <Grid
            key={index}
            xs={12}
            sm={6}
            md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ color: card.color }}>
                  {card.content}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={card.onClick}>
                  {card.action}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const POSDashboardWithProviders: React.FC = () => {
  return (
    <POSProvider>
      <POSLayout>
        <POSDashboard />
      </POSLayout>
    </POSProvider>
  );
};

export default POSDashboardWithProviders; 